/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// Third-party library imports
import { get } from 'lodash-es';
import { print } from 'graphql';
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';

// Local imports - Constants
import { DEFAULT_CURRENCY_CODE, DEFAULT_LOCALE } from '../../config/config';
import { currencyCodes } from '../../constants/companyGeneralOptions';

// Local imports - Project-specific modules - components / types
import { AppCommonState, PageView } from './types';

// Local imports - Utility functions / interfaces
import { DynamicObject, ResponseModalObject } from '../../utils/commonInterfaces';

// Local imports - Redux actions / store
import type { RootState } from '../storeTypes';

// Type-safe initialState!
export const initialState: AppCommonState = {
    loadLuckysheet: false,
    refetchPageViews: false,
    defaultPageViewValue: undefined,
    attachmentEditing: false,
    locale: DEFAULT_LOCALE,
    currencyCode: DEFAULT_CURRENCY_CODE,
    demoOptionsConfig: {},
    pageViewsSelected: {},
};

const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        updateRefetchPageViewsAction: {
            reducer: (
                state, 
                action: PayloadAction<{
                    willRefetch: boolean, 
                    defaultPageViewValue?: string
                }>
            ) => {
                const { willRefetch, defaultPageViewValue } = action.payload;
                state.refetchPageViews = willRefetch;
                state.defaultPageViewValue = defaultPageViewValue;
            },
            prepare: (willRefetch: boolean, defaultPageViewValue?: string) => {
                return {
                    payload: {
                        willRefetch,
                        defaultPageViewValue
                    }
                };
            }
        },
        updatePageViewsSelectedAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    pageName: string,
                    pageView: any
                }>
            ) => {
                const { pageName, pageView } = action.payload;
                state.pageViewsSelected[pageName] = pageView;
            },
            prepare: (pageName: string, pageView: any) => {
                return {
                    payload: {
                        pageName,
                        pageView
                    }
                }
            }
        },
        updateAttachmentEditingAction: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.attachmentEditing = action.payload;
        },
        setLocaleAction: (
            state,
            action: PayloadAction<string>
        ) => {
            const usedLocale = action.payload;
            const localeArray = usedLocale.split('-');
            const country = get(localeArray, 1);
            const currencyCode = get(currencyCodes, country);

            state.locale = usedLocale;
            state.currencyCode = currencyCode;    
        },
        updateDemoOptionsConfigAction: (
            state,
            action: PayloadAction<DynamicObject[]>
        ) => {
            state.demoOptionsConfig = {
                ...action.payload
            };
        },
        loadLuckysheetAction: (
            state
        ) => {
            state.loadLuckysheet = true;
        },
    }
});

const { actions, reducer } = commonSlice;

// Actions
export const {
    updateRefetchPageViewsAction,
    updatePageViewsSelectedAction,
    updateAttachmentEditingAction,
    setLocaleAction,
    updateDemoOptionsConfigAction,
    loadLuckysheetAction
} = actions;

export const getDemoOptionsConfigAction = createAction(
    'common/constgetDemoOptionsConfigAction'
);

export const downloadToExcelAction = createAction(
    'common/downloadToExcelAction',
    function prepare(
        payload: DynamicObject,
        callback?: (response: ResponseModalObject) => void
    ) {
        const { Query } = payload;
        return {
            payload: { 
                payload: {
                    ...payload,
                    Query: typeof Query === 'object' ? print(Query) : Query,
                }, 
                callback 
            },
        };
    }
);

export const getPresignedUrlDownloadReportReadyAction = createAction(
    'common/getPresignedUrlDownloadReportReadyAction',
    function prepare(
        payload: DynamicObject,
        callback?: (response: DynamicObject) => void,
        refetchCount: number = 1
    ) {
        return {
            payload: { payload, callback, refetchCount },
        };
    }
);

export const getCustomFieldsFilterListByTypes = createAction(
    'common/getCustomFieldsFilterListByTypes',
    function prepare(
        customFieldTypes: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { customFieldTypes, callback },
        };
    }
);

export const getCustomFieldsFilterListByTypesForOrganisation = createAction(
    'common/getCustomFieldsFilterListByTypesForOrganisation',
    function prepare(
        companyIds: string[],
        customFieldTypes: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { companyIds, customFieldTypes, callback },
        };
    }
);

export const getAttachmentPresignedUrlAction = createAction(
    'common/getAttachmentPresignedUrlAction',
    function prepare(
        payload: DynamicObject,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const deleteAttachmentAction = createAction(
    'common/deleteAttachmentAction',
    function prepare(
        payload: DynamicObject,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const fetchPageAppliedFiltersViewListRequestAction = createAction(
    'common/fetchPageAppliedFiltersViewListRequestAction',
    function prepare(
        pageName: string,
        isDashboard: boolean,
        notSameCompany: string | boolean,
        callback?: (data: PageView[]) => void
    ) {
        return {
            payload: { pageName, isDashboard, notSameCompany, callback },
        };
    }
);

export const deleteAppliedFiltersViewAction = createAction(
    'common/deleteAppliedFiltersViewAction',
    function prepare(
        payload: { Id: string; PageName: string | undefined },
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const saveAppliedFiltersViewAction = createAction(
    'common/saveAppliedFiltersViewAction',
    function prepare(
        payload: any,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

// Selectors
export const getAppState = (state: RootState) => state.app;
export const getRefetchPageViews = createSelector(
    getAppState,
    (app) => app.refetchPageViews
);
export const getDemoOptionsConfig = createSelector(
    getAppState,
    (app) => app.demoOptionsConfig
);
export const getLoadLuckysheet = createSelector(
    getAppState,
    (app) => app.loadLuckysheet
)
export const getLocale = createSelector(
    getAppState,
    (app) => app.locale
);
export const getDefaultPageViewValue = createSelector(
    getAppState,
    (app) => app.defaultPageViewValue
);
export const getIsAttachmentEditingActive = createSelector(
    getAppState,
    (app) => app.attachmentEditing
);
export const getCurrencyCode = createSelector(
    getAppState,
    (app) => app.currencyCode
);
export const getPageViewsSelected = createSelector(
    getAppState,
    (app) => app.pageViewsSelected
);

// Reducer
export default reducer;